/**
 * FindingsTablePage - Phase 5: Production Investigation Queue
 * 
 * Dense, sortable, filterable findings table for large result sets.
 * Engineers can quickly find, filter, sort, triage, and open findings.
 * 
 * Features:
 * - Risk-based default ordering (severity → recency)
 * - Sortable columns (severity, created, updated, title, confidence)
 * - Multi-select severity filter
 * - Comprehensive filters (status, project, audit, track, category, confidence, search)
 * - URL state persistence for reload/shareability
 * - Server-side pagination
 * - Accessible table with ARIA roles and keyboard navigation
 * - Dense technical UI optimized for 1440x900 and 1366x768
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, FindingsResponse, FindingsFilters, FindingListItem } from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { EmptyState } from '../components/EmptyState'
import { SeverityBadge } from '../components/SeverityBadge'
import { StatusBadge } from '../components/StatusBadge'
import { Pagination } from '../components/Pagination'
import { useLocale } from '../contexts/LocaleContext'
import { confidenceLabel, severityLabel, statusLabel, trackLabel } from '../lib/i18n'

type SortColumn = 'severity' | 'created_at' | 'updated_at' | 'title' | 'confidence'
type SortOrder = 'asc' | 'desc'

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'info'] as const
const STATUS_OPTIONS = ['open', 'acknowledged', 'resolved', 'false_positive', 'wont_fix', 'duplicate'] as const
const CONFIDENCE_OPTIONS = ['high', 'medium', 'low', 'uncertain'] as const

export function FindingsTablePage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<FindingsResponse | null>(null)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  
  // Parse filters from URL
  const filters: FindingsFilters = useMemo(() => {
    const severities = searchParams.get('severity')?.split(',').filter(Boolean)
    return {
      page: parseInt(searchParams.get('page') || '1'),
      page_size: parseInt(searchParams.get('page_size') || '50'),
      severity: severities && severities.length > 0 ? severities.join(',') : undefined,
      status: searchParams.get('status') || undefined,
      confidence: searchParams.get('confidence') || undefined,
      project_id: searchParams.get('project_id') || undefined,
      audit_run_id: searchParams.get('audit_run_id') || undefined,
      track: searchParams.get('track') || undefined,
      category: searchParams.get('category') || undefined,
      search: searchParams.get('search') || undefined,
      sort: searchParams.get('sort') as SortColumn || undefined,
      order: searchParams.get('order') as SortOrder || undefined,
    }
  }, [searchParams])
  
  // Detect if filters are active (excluding page/page_size)
  const hasActiveFilters = useMemo(() => {
    return !!(filters.severity || filters.status || filters.confidence || 
              filters.project_id || filters.audit_run_id || filters.track || 
              filters.category || filters.search)
  }, [filters])
  
  // Load findings
  useEffect(() => {
    loadFindings()
  }, [searchParams])
  
  async function loadFindings() {
    try {
      setLoading(true)
      setError(null)
      const result = await api.listFindings(filters)
      setData(result)
    } catch (err) {
        setError(err instanceof Error ? err.message : t('findings.failedLoad'))
    } finally {
      setLoading(false)
    }
  }
  
  // Update URL with new filters
  function updateFilters(updates: Partial<FindingsFilters>, resetPage = true) {
    const params = new URLSearchParams(searchParams)
    
    // Reset to page 1 when filters change
    if (resetPage) {
      params.set('page', '1')
    }
    
    // Apply updates
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        params.delete(key)
      } else {
        params.set(key, String(value))
      }
    })
    
    setSearchParams(params)
  }
  
  // Clear all filters
  function clearAllFilters() {
    setSearchParams(new URLSearchParams())
  }
  
  // Toggle severity in multi-select
  function toggleSeverity(severity: string) {
    const currentSeverities = filters.severity?.split(',').filter(Boolean) || []
    const newSeverities = currentSeverities.includes(severity)
      ? currentSeverities.filter(s => s !== severity)
      : [...currentSeverities, severity]
    
    updateFilters({ severity: newSeverities.length > 0 ? newSeverities.join(',') : undefined })
  }
  
  // Sort column
  function handleSort(column: SortColumn) {
    const currentSort = filters.sort
    const currentOrder = filters.order || 'asc'
    
    if (currentSort === column) {
      // Toggle order if same column
      updateFilters({ order: currentOrder === 'asc' ? 'desc' : 'asc' }, false)
    } else {
      // New column, default to asc (except severity/confidence which default to asc for high-to-low priority)
      const defaultOrder = (column === 'severity' || column === 'confidence') ? 'asc' : 'desc'
      updateFilters({ sort: column, order: defaultOrder }, false)
    }
  }
  
  // Navigate to finding detail
  function handleRowClick(finding: FindingListItem) {
    navigate(`/finding/${finding.id}`)
  }
  
  // Keyboard navigation
  function handleRowKeyDown(e: React.KeyboardEvent, finding: FindingListItem) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleRowClick(finding)
    }
  }
  
  // Render sort indicator
  function renderSortIndicator(column: SortColumn) {
    if (filters.sort !== column) {
      return <span className="text-[var(--text-tertiary)] ml-1">↕</span>
    }
    return filters.order === 'asc' ? ' ↑' : ' ↓'
  }
  
  // Truncate long paths
  function truncatePath(path: string | null, maxLength = 50): string {
    if (!path) return '-'
    if (path.length <= maxLength) return path
    const start = path.slice(0, maxLength / 2 - 2)
    const end = path.slice(-maxLength / 2 + 2)
    return `${start}...${end}`
  }
  
  if (loading) {
    return (
      <div className="space-y-4" data-testid="findings-page">
        <h1 className="text-2xl font-medium mb-1">{t('nav.findings')}</h1>
        <LoadingSpinner message={t('findings.loading')} />
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="card card-error">
        <p className="text-sm">{error}</p>
        <button onClick={loadFindings} className="btn btn-sm btn-primary mt-4">
          {t('common.retry')}
        </button>
      </div>
    )
  }
  
  const selectedSeverities = filters.severity?.split(',').filter(Boolean) || []
  
  return (
    <div className="space-y-4" data-testid="findings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-medium mb-1">{t('nav.findings')}</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          {t('findings.count', { n: data?.total || 0 })}{hasActiveFilters ? ` ${t('findings.filtered')}` : ''}
        </p>
      </div>
      
      {/* Filters */}
      <div className="card p-4 space-y-3">
        {/* Primary Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder={t('findings.searchPh')}
              value={filters.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="input input-sm w-full"
              aria-label={t('findings.searchAria')}
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) => updateFilters({ status: e.target.value || undefined })}
            className="input input-sm"
            aria-label={t('findings.statusAria')}
          >
            <option value="">{t('findings.allStatuses')}</option>
            {STATUS_OPTIONS.map(status => (
              <option key={status} value={status}>
                {statusLabel(locale, status)}
              </option>
            ))}
          </select>
          
          {/* Confidence Filter */}
          <select
            value={filters.confidence || ''}
            onChange={(e) => updateFilters({ confidence: e.target.value || undefined })}
            className="input input-sm"
            aria-label={t('findings.confidenceAria')}
          >
            <option value="">{t('findings.allConfidence')}</option>
            {CONFIDENCE_OPTIONS.map(conf => (
              <option key={conf} value={conf}>
                {confidenceLabel(locale, conf)}
              </option>
            ))}
          </select>
          
          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="btn btn-sm btn-secondary"
            aria-expanded={showAdvancedFilters}
          >
            {showAdvancedFilters ? t('findings.hideAdvanced') : t('findings.showAdvanced')}
          </button>
        </div>
        
        {/* Severity Multi-Select (always visible) */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">
            {t('findings.severity')}
          </label>
          <div className="flex flex-wrap gap-2">
            {SEVERITY_OPTIONS.map(severity => {
              const isSelected = selectedSeverities.includes(severity)
              return (
                <button
                  key={severity}
                  type="button"
                  onClick={() => toggleSeverity(severity)}
                  className="severity-chip"
                  style={{ ['--chip-color' as string]: `var(--reviewer-severity-${severity})` }}
                  aria-pressed={isSelected}
                  aria-label={t('findings.filterSeverityAria', { severity })}
                >
                  {locale === 'en' ? severity.toUpperCase() : severityLabel(locale, severity)}
                </button>
              )
            })}
          </div>
        </div>
        
        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t border-[var(--border)]">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('findings.colTrack')}
              </label>
              <input
                type="text"
                placeholder={t('findings.trackPh')}
                value={filters.track || ''}
                onChange={(e) => updateFilters({ track: e.target.value || undefined })}
                className="input input-sm w-full"
                aria-label={t('findings.filterTrackAria')}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('findings.category')}
              </label>
              <input
                type="text"
                placeholder={t('findings.categoryPh')}
                value={filters.category || ''}
                onChange={(e) => updateFilters({ category: e.target.value || undefined })}
                className="input input-sm w-full"
                aria-label={t('findings.filterCategoryAria')}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('findings.projectId')}
              </label>
              <input
                type="text"
                placeholder={t('findings.uuidPh')}
                value={filters.project_id || ''}
                onChange={(e) => updateFilters({ project_id: e.target.value || undefined })}
                className="input input-sm w-full"
                aria-label={t('findings.filterProjectAria')}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                {t('findings.auditId')}
              </label>
              <input
                type="text"
                placeholder={t('findings.uuidPh')}
                value={filters.audit_run_id || ''}
                onChange={(e) => updateFilters({ audit_run_id: e.target.value || undefined })}
                className="input input-sm w-full"
                aria-label={t('findings.filterAuditAria')}
              />
            </div>
          </div>
        )}
        
        {/* Active Filter Summary */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
            <div className="text-xs text-[var(--text-secondary)]">
              {Object.entries({
                severity: selectedSeverities.length > 0 ? t('findings.summarySeverities', { n: selectedSeverities.length }) : null,
                status: filters.status ? t('findings.summaryStatus', { v: statusLabel(locale, filters.status) }) : null,
                confidence: filters.confidence ? t('findings.summaryConfidence', { v: confidenceLabel(locale, filters.confidence) }) : null,
                track: filters.track ? t('findings.summaryTrack', { v: filters.track }) : null,
                category: filters.category ? t('findings.summaryCategory', { v: filters.category }) : null,
                search: filters.search ? t('findings.summarySearch', { v: filters.search }) : null,
              })
                .filter(([_, value]) => value)
                .map(([_, value]) => value)
                .join(' • ')}
            </div>
            <button
              onClick={clearAllFilters}
              className="text-xs text-[var(--accent)] hover:underline"
              aria-label={t('findings.clearAria')}
            >
              {t('findings.clearAll')}
            </button>
          </div>
        )}
      </div>
      
      {/* Table */}
      {data && data.findings.length === 0 ? (
        <EmptyState
          title={hasActiveFilters ? t('findings.noMatching') : t('findings.emptyTitle')}
          description={
            hasActiveFilters
              ? t('findings.noMatchingBody')
              : t('findings.emptyBody')
          }
        />
      ) : (
        <>
          <div className="card p-0">
            <div className="overflow-x-auto">
              <table 
                className="w-full text-sm"
                role="table"
                aria-label={t('findings.tableAria')}
              >
                <thead className="bg-[var(--bg-secondary)] sticky top-0">
                  <tr role="row">
                    <th
                      role="columnheader"
                      aria-sort={filters.sort === 'severity' ? (filters.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                      onClick={() => handleSort('severity')}
                    >
                      {t('findings.severity')}{renderSortIndicator('severity')}
                    </th>
                    <th
                      role="columnheader"
                      aria-sort={filters.sort === 'title' ? (filters.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                      onClick={() => handleSort('title')}
                    >
                      {t('findings.title')}{renderSortIndicator('title')}
                    </th>
                    <th
                      role="columnheader"
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)]"
                    >
                      {t('findings.colTrack')}
                    </th>
                    <th
                      role="columnheader"
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)]"
                    >
                      {t('findings.colLocation')}
                    </th>
                    <th
                      role="columnheader"
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)]"
                    >
                      {t('findings.project')}
                    </th>
                    <th
                      role="columnheader"
                      aria-sort={filters.sort === 'confidence' ? (filters.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                      onClick={() => handleSort('confidence')}
                    >
                      {t('review.confidence')}{renderSortIndicator('confidence')}
                    </th>
                    <th
                      role="columnheader"
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)]"
                    >
                      {t('findings.status')}
                    </th>
                    <th
                      role="columnheader"
                      aria-sort={filters.sort === 'created_at' ? (filters.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="px-3 py-2 text-left text-xs font-medium text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors"
                      onClick={() => handleSort('created_at')}
                    >
                      {t('findings.colCreated')}{renderSortIndicator('created_at')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.findings.map((finding) => (
                    <tr
                      key={finding.id}
                      role="row"
                      tabIndex={0}
                      onClick={() => handleRowClick(finding)}
                      onKeyDown={(e) => handleRowKeyDown(e, finding)}
                      className="border-t border-[var(--border)] hover:bg-[var(--bg-secondary)] cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-inset"
                      aria-label={t('findings.rowAria', { title: finding.title, severity: finding.severity, status: finding.status })}
                    >
                      <td className="px-3 py-2">
                        <SeverityBadge severity={finding.severity} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-[var(--text-primary)]">
                          {finding.title}
                        </div>
                        {finding.tags.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {finding.tags.slice(0, 2).map(tag => (
                              <span 
                                key={tag} 
                                className="text-xs px-1.5 py-0.5 bg-[var(--bg-tertiary)] text-[var(--text-secondary)] rounded"
                              >
                                {tag}
                              </span>
                            ))}
                            {finding.tags.length > 2 && (
                              <span className="text-xs text-[var(--text-tertiary)]">
                                +{finding.tags.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[var(--text-secondary)]">
                        {trackLabel(locale, finding.track)}
                      </td>
                      <td className="px-3 py-2 text-[var(--text-secondary)] font-mono text-xs">
                        <span title={finding.primary_location || undefined}>
                          {truncatePath(finding.primary_location)}
                        </span>
                        {finding.line_number && (
                          <span className="text-[var(--text-tertiary)]">:{finding.line_number}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[var(--text-secondary)] text-xs">
                        {finding.project_name}
                      </td>
                      <td className="px-3 py-2">
                        <span 
                          className={`text-xs font-medium ${
                            finding.confidence === 'high' ? 'text-[var(--status-success)]' :
                            finding.confidence === 'medium' ? 'text-[var(--text-secondary)]' :
                            'text-[var(--text-tertiary)]'
                          }`}
                        >
                          {confidenceLabel(locale, finding.confidence)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={finding.status} className="text-xs" />
                      </td>
                      <td className="px-3 py-2 text-[var(--text-secondary)] text-xs whitespace-nowrap">
                        {new Date(finding.created_at).toLocaleDateString(locale === 'en' ? 'en-US' : 'ru-RU', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <Pagination
              currentPage={data.page}
              totalPages={data.total_pages}
              onPageChange={(page) => updateFilters({ page }, false)}
            />
          )}
        </>
      )}
    </div>
  )
}
